/**
 * @module components/NetworksTable
 * @author Charles Blais <charles.blais@canada.ca>
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ControlLabel, FormGroup, Table } from 'react-bootstrap';
import { useTable, useFilters, useSortBy } from 'react-table';

import { getStatusIndicator } from '../../libs/display';
import { useLanguageState } from '../../contexts/LanguageProvider';
import { useAppState } from '../../contexts/AppStateProvider';

import { Channels } from '../../libs/networks';
import {
  DefaultColumnFilter,
  SelectColumnFilter,
} from '../../libs/react-table-filters';


const ChannelsReactTable = ({columns, data}) => {

  const [ lang ] = useLanguageState();
  const [ state ] = useAppState();

  // Column hook
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }), []);

  // Table hook
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
    defaultColumn,
    initialState: {
      sortBy: [
          {
              id: 'endtime',
              desc: false
          }
      ]
    }
  }, useFilters, useSortBy);

  // Generate the cell property
  const getCellProps = (cell) => {
    if(cell.column.Header === lang.status) {
      return {
        style: {
          backgroundColor: (cell.value === lang.close) ? state.colorClose : state.colorOpen,
        }
      }
    }
    return { style: {} };
  };

  return (
    <Table {...getTableProps()} bordered hover>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, idx) => {
              const sort = (
                <span>
                  {column.isSorted
                    ? column.isSortedDesc
                      ? ' 🔽'
                      : ' 🔼'
                    : ''}
                </span>
              );
              return (
                <th {
                  ...column.getHeaderProps(
                    (!column.Filter.name.includes('Select'))
                    ?column.getSortByToggleProps()
                    :{}
                  )}
                >
                  {column.canFilter ? (
                    <FormGroup
                      controlId={`id${idx}`}
                    >
                      <ControlLabel>{column.render('Header')}{sort}</ControlLabel>
                      {column.render('Filter')}
                    </FormGroup>
                  ) : <>{column.render('Header')}{sort}</>}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell, j) => (
                <td {...cell.getCellProps([{
                    className: cell.column.className,
                    style: cell.column.style,
                  }, getCellProps(cell)])}>
                  {cell.render('Cell')}
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
    </Table>
  );
};


const ChannelsTableGeomag = ({channels}) => {
  const [ lang ] = useLanguageState();
  const [ state ] = useAppState();

  // Create column information
  const columns = useMemo(() => [{
    Header: lang.channel,
    accessor: 'code',
  }, {
    Header: lang.location,
    accessor: 'locationCode',
    Filter: SelectColumnFilter,
  }, {
    Header: lang.startDate,
    accessor: (originalRow) => 
      (originalRow.starttime) ? originalRow.starttime.format(state.datetimeFormat) : '',
  }, {
    Header: lang.endDate,
    id: 'endtime',
    accessor: (originalRow) =>
      (originalRow.endtime) ? originalRow.endtime.format(state.datetimeFormat) : '',
  }, {
    Header: lang.status,
    accessor: (originalRow) => {
      switch(getStatusIndicator(originalRow)){
        case -1: return lang.close;
        case 0: return lang.open;
        case 1: return lang.upcoming;
        default: return '';
      }
    },
    Filter: SelectColumnFilter,
  }, {
    Header: lang.samplePeriod,
    accessor: 'samplePeriod',
    Filter: SelectColumnFilter,
  }], [
    state.datetimeFormat,
    lang.open,
    lang.close,
    lang.upcoming,
    lang.channel,
    lang.location,
    lang.startDate,
    lang.endDate,
    lang.samplePeriod,
    lang.status
  ]);

  const data = useMemo(() => channels.sort((a, b) => b.starttime - a.starttime), [channels]);

  return (
    <ChannelsReactTable
      columns={columns}
      data={data}
    />
  );
};
  
  
ChannelsTableGeomag.propTypes = {
  /**
   * Channels object taken from a Station object
   */
  channels: PropTypes.instanceOf(Channels),
};


ChannelsTableGeomag.defaultProps = {
  channels: [],
};
  
  
export default ChannelsTableGeomag;
